"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { trackCompareEvent } from "@/lib/compare/analytics";

/**
 * Phase 50 (HUB-01) — hub-specific click delegator.
 *
 * Listens for clicks on `a[data-compare-event="hub_card"]` anywhere in the
 * hub DOM and emits `compare_hub_card_clicked` with the target slug + kind.
 *
 * The hub is NOT a ComparePage, so the shared <CompareTracker> (which
 * requires a `page` prop for slug/kind/reviewedAt injection) isn't
 * applicable here. This is a minimal ~40 LoC delegator that only handles
 * hub-specific card clicks.
 *
 * Consent: we trust posthog.__loaded + the consent plumbing in
 * components/analytics/posthog-provider.tsx — no duplicate guard here.
 * See 49-CONTEXT.md Grey Area 1 "Zero new consent plumbing".
 */
export function CompareHubTracker() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>(
        'a[data-compare-event="hub_card"]',
      );
      if (!anchor) return;

      const slug = anchor.getAttribute("data-compare-slug");
      const kind = anchor.getAttribute("data-page-kind");
      if (!slug || !kind) return;
      if (kind !== "vs" && kind !== "roundup" && kind !== "alternatives") {
        return;
      }

      trackCompareEvent({
        event: "compare_hub_card_clicked",
        target_slug: slug,
        target_kind: kind,
      });
    }

    document.addEventListener("click", onClick);

    // Touch posthog to document the capture-path dependency (the wrapper
    // handles the __loaded guard itself — see lib/compare/analytics.ts).
    void posthog;

    return () => {
      document.removeEventListener("click", onClick);
    };
  }, []);

  return null;
}
